import type { TFunction } from 'i18next'

import type { GroupedPort } from '@/features/resource/utils/portGroups'

/**
 * The accessible name and tooltip for the control that copies a port's id.
 *
 * Shared rather than chosen at each call site: the table and the graph's field panel both offer this
 * copy, and they were wording it differently — the table naming the port, the panel not. Two places
 * making the same choice separately is how they drift, and the label is the only thing telling one
 * of these controls from the sixteen others on the page.
 *
 * The port's name goes into the label because a control whose visible text is a name must be
 * addressable by that name (WCAG 2.5.3), and the id goes in whole because nothing else shows it.
 */
export function portCopyIdLabel(port: GroupedPort, t: TFunction<'translation'>): string {
  if (port.name === null) {
    return t(
      port.kind === 'CONTROL'
        ? 'resources.detail_panel.copy_control_port_id'
        : 'resources.detail_panel.copy_metering_port_id',
      { portId: port.id },
    )
  }

  return t(
    port.kind === 'CONTROL'
      ? 'resources.detail_panel.copy_named_control_port_id'
      : 'resources.detail_panel.copy_named_metering_port_id',
    { portId: port.id, portName: port.name },
  )
}
